import React from "react";
import { createOidcConfig, type SigninState } from "../auth.ts";
import i18n from "../i18n/config.ts";
import { AuthProvider } from "react-oidc-context";
import type { User } from "oidc-client-ts";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { darkTheme } from "@mbits/mray-react-common";
import { I18nextProvider } from "react-i18next";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { SnackbarProvider } from "@/contexts/SnackbarContext.tsx";
import { router } from "../router.ts";

/**
 * AppProviders component
 *
 * Provides the necessary context providers for the application.
 *
 * @component
 * @example
 * return <AppProviders />;
 */

const queryClient = new QueryClient({
  // Required for error handling in queries, otherwise errors are swallowed and not logged to console
  queryCache: new QueryCache({
    onError: (error, query) => {
      console.error(`Error in query ${query.queryKey}:`, error);
    },
  }),
});

const AppProviders: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Authentication configuration
  const apps = window.ENV.appSwitcher;

  const resourceIds = apps.map((app) => app.resourceId).filter((id) => id);

  const createProjectScopes = resourceIds
    .map((id: string) => `urn:zitadel:iam:org:project:id:${id}:aud`)
    .join(" ");

  const oidcConfig = createOidcConfig({
    oidcAuthority: window.ENV.oidcAuthority,
    oidcClientId: window.ENV.oidcClientId,
    oidcScope: window.ENV.oidcScope + " " + createProjectScopes,
    basePath: window.ENV.basePath,
    onSigninCallback: (user: User | undefined): void => {
      // Restore the original URL (with studies/tag params) after OIDC redirect,
      // so validateSearch can normalise them as if the user had navigated there directly.
      const { returnHref } = (user?.state ?? {}) as SigninState;

      if (returnHref) {
        void router.navigate({ href: returnHref, replace: true });
        return;
      }

      window.history.replaceState({}, "", window.location.pathname);
    },
  });

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <I18nextProvider i18n={i18n}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <AuthProvider {...oidcConfig}>
            <SnackbarProvider>
              <QueryClientProvider client={queryClient}>
                {children}
              </QueryClientProvider>
            </SnackbarProvider>
          </AuthProvider>
        </LocalizationProvider>
      </I18nextProvider>
    </ThemeProvider>
  );
};

export default AppProviders;
